let analyticsPromise;
let analyticsModulePromise;
let firebaseAppPromise;
let analyticsStartupPromise;
let analyticsStartupScheduled = false;
let queuedPageViewPath = null;
const queuedButtonEvents = [];
const ANALYTICS_START_DELAY_MS = 8000;

const getAnalyticsModule = async () => {
  if (!analyticsModulePromise) {
    analyticsModulePromise = import("firebase/analytics");
  }

  return analyticsModulePromise;
};

const getFirebaseAppInstance = async () => {
  if (!firebaseAppPromise) {
    firebaseAppPromise = import("./firebaseApp").then(({ getFirebaseApp }) =>
      getFirebaseApp()
    );
  }

  return firebaseAppPromise;
};

const getAnalyticsInstance = async () => {
  if (typeof window === "undefined") {
    return null;
  }

  if (!analyticsPromise) {
    analyticsPromise = getAnalyticsModule()
      .then(async ({ getAnalytics, isSupported }) => {
        const supported = await isSupported().catch(() => false);

        if (!supported) {
          return null;
        }

        return getAnalytics(await getFirebaseAppInstance());
      })
      .catch(() => null);
  }

  return analyticsPromise;
};

const flushQueuedEvents = async () => {
  const analytics = await getAnalyticsInstance();

  if (!analytics || typeof window === "undefined") {
    return;
  }

  const { logEvent } = await getAnalyticsModule();
  const pendingPageViewPath = queuedPageViewPath;

  if (pendingPageViewPath) {
    queuedPageViewPath = null;
    const pageTitle = typeof document !== "undefined" ? document.title : "";

    logEvent(analytics, "page_view", {
      page_path: pendingPageViewPath,
      page_location: window.location.href,
      page_title: pageTitle,
    });
  }

  while (queuedButtonEvents.length > 0) {
    const eventPayload = queuedButtonEvents.shift();

    if (!eventPayload) {
      continue;
    }

    logEvent(analytics, "button_click", {
      button_name: eventPayload.buttonName,
      button_location: eventPayload.buttonLocation,
      button_target: eventPayload.buttonTarget,
      page_path: window.location.pathname,
    });
  }
};

const startAnalytics = () => {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  analyticsStartupScheduled = false;

  if (!analyticsStartupPromise) {
    analyticsStartupPromise = flushQueuedEvents().catch(() => undefined);
  } else {
    analyticsStartupPromise = analyticsStartupPromise.then(() =>
      flushQueuedEvents().catch(() => undefined)
    );
  }

  return analyticsStartupPromise;
};

const scheduleAnalyticsStartup = () => {
  if (
    typeof window === "undefined" ||
    analyticsStartupPromise ||
    analyticsStartupScheduled
  ) {
    return;
  }

  analyticsStartupScheduled = true;

  const bootAnalytics = () => {
    analyticsStartupScheduled = false;
    window.clearTimeout(fallbackTimerId);
    interactionEvents.forEach((eventName) => {
      window.removeEventListener(eventName, bootAnalytics, listenerOptions);
    });
    startAnalytics();
  };
  const interactionEvents = ["pointerdown", "keydown", "touchstart"];
  const listenerOptions = { passive: true, once: true };
  const fallbackTimerId = window.setTimeout(
    bootAnalytics,
    ANALYTICS_START_DELAY_MS
  );

  interactionEvents.forEach((eventName) => {
    window.addEventListener(eventName, bootAnalytics, listenerOptions);
  });
};

export const queuePageView = (path) => {
  if (!path) {
    return;
  }

  queuedPageViewPath = path;

  if (analyticsStartupPromise) {
    startAnalytics();
    return;
  }

  scheduleAnalyticsStartup();
};

export const trackButtonClick = ({ buttonName, buttonLocation, buttonTarget }) => {
  queuedButtonEvents.push({
    buttonName,
    buttonLocation,
    buttonTarget,
  });

  return startAnalytics();
};
